// folder-cols.jsx — the ONE folder/hierarchy grid column set (UI_VISION §10:
// "Folder grid | 2 forks | easiest win", forced now by §8.0 — the Onshape screen
// is the first surface that needs the catalog AND product grids at once, so the
// column set has to be genuinely shared, not copied a third time).
//
// One builder, three consumers:
//   • Catalog folder grid  (App.jsx, non-L3 levels)       — editable, Items = direct children
//   • Products hierarchy grid (ProductsView.jsx)          — editable, Items = products beneath,
//                                                           product rows get status badge + ↗
//   • Onshape Main grid    (OnshapeView.jsx)              — read-only, mixed category/leaf rows
//
// Column order standard (Alex 2026-07-02): # · Parent · Tag · Description · …
// Variants are FLAGS on this one builder, never a re-written column list
// (UI_VISION §4 "Flags, not forks").

import { StatusCounts } from './components/TreeNav.jsx';
import { ProductStatusCell } from './product-cols.jsx';
import StatusCell from './components/StatusCell.jsx';
import { OnshapeLink } from './OnshapeLink.jsx';
import ImageCell from './components/ImageCell.jsx';
import { WARN } from './icons.js';
import OwnerLabel from './components/OwnerLabel.jsx';
import Text from './components/Text.jsx';

// ⚠ N formula-error chip on product rows — cascaded from the tree (Alex 2026-07-02).
// Lived inline in ProductsView's hierarchyCols; hoisted here with the column.
function ErrorCountChip({ n }) {
  if (!n) return null;
  return (
    <span
      title={`${n} problem line(s): formula errors, missing prices, or hours with no labour line. Open the product to see the ${WARN} lines`}
      style={{
        background: 'var(--tg-glyph-bg)', color: 'var(--tg-warn-color)',
        border: 'var(--tg-glyph-border)', borderRadius: 'var(--tg-glyph-radius)',
        padding: 'var(--tg-glyph-pad)', fontSize: 'var(--fs-micro)', fontWeight: 700,
        lineHeight: 1.4, whiteSpace: 'nowrap',
      }}
    >{WARN} {n}</span>
  );
}

// Status cell for a PRODUCT row (leaf): lifecycle badge + optional ⚠ error chip.
export function ProductLeafStatus({ row }) {
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <ProductStatusCell value={row.status} />
      <ErrorCountChip n={row.error_count} />
    </span>
  );
}

// Status cell for a CATEGORY row: per-status quantity chips (DECISION, Alex
// 2026-07-02: quantities, not the bare rollup glyph). An empty category renders
// StatusCounts' own ∅ empty chip — unified 2026-07-19: the products grid used a
// muted dash for the same state while the catalog grid showed ∅; one look now.
function CategoryStatus({ row }) {
  return <StatusCounts counts={row.counts || {}} />;
}

// makeFolderCols(opts) → DataGrid columns for a folder/hierarchy level.
//   seq          — true SHOWS the '#' column (default false, 2026-08-23: the
//                  sequence is display-only, never stored or read back — Alex,
//                  "if not required, remove the # row number from the output" —
//                  so it's opt-IN now, not opt-out; no current caller sets it)
//   seqKey       — row field holding the 1-based sequence ('_seq' | '_pos')
//   parentCol    — screen-specific "Parent" column object (CatalogReparent /
//                  PathDropdowns), inserted right after '#'; null = none
//   editable     — Tag/Description editable + addField (read-only surfaces: false)
//   items        — { value: row => number } | null (null = no Items column;
//                  the product-leaf grid has none). Column key is '_items'.
//   statusMode   — 'counts' (category rows) | 'leaf' (product rows) |
//                  'auto' (branch per row._type — mixed grids, e.g. Onshape)
//   statusEditable — leaf status becomes a select editor (draft/active/
//                  deprecated). The product HEADER row uses this (§5: "same
//                  columns, same editors" — the editor lives with the ONE
//                  shared column instead of a second bespoke definition).
//   onshapeCol   — append the compact ↗ OnshapeLink column (rows carry onshape_ref)
//   extraCols    — appended at the end (cost_class, Prj Qty/Cost, …)
export function makeFolderCols({
  seq = false,
  seqKey = '_seq',
  parentCol = null,
  editable = true,
  items = { value: row => row.count },
  statusMode = 'counts',
  statusEditable = false,
  onshapeCol = false,
  extraCols = [],
  // INF-8 — (row) => Promise: offer "Promote to Standard" on a project's own row.
  // Internal callers only; null hides it.
  onPromote = null,
} = {}) {
  const cols = [];

  // THE PICTURE, FIRST (Alex, 2026-08-27: "move the image to left of each row.
  // Because this will make the demo attractive"). It used to sit near the end,
  // beside the ↗ link, and only on the two grids that passed onshapeCol — so the
  // products hierarchy had one and the catalog folders did not, for no reason
  // anyone chose. It is unconditional now: a column that appears on some grids
  // and not others is how a row ends up looking like a different kind of thing.
  //
  // Which row gets which key is decided from statusMode + _type, because that is
  // already how this builder knows what its rows ARE. Category rows resolve to
  // nothing and render nothing — a folder has no picture, and inventing one
  // (the first child's? a folder glyph?) would be decoration, not information.
  cols.push({
    // width: ImageCell's own default size (28) + its 1px border ×2 + the cell's
    // own padding (--th-cell-pad, 8px ×2) = 46 — narrower clipped it: measured
    // live at 34, the image overflowed the cell by several px and got clipped
    // asymmetrically (Alex, 2026-08-27: "the 3D pictures... looking truncated &
    // offset"). 48 for a little slack rather than an exact fit.
    key: '_img', label: '', width: 48, align: 'center', resizable: false,
    render: (row) => {
      const type = statusMode === 'auto' ? row._type : (statusMode === 'leaf' ? 'product' : 'category');
      const dbId = type === 'product' ? `prod:${row.id}`
        : type === 'family' ? `fam:${row.family_id ?? row.id}`
          : null;
      if (!dbId) return null;
      // Bleed through the cell's own padding (--th-cell-pad, currently 8px each
      // side) down to a small 2px gutter, same idea TextCell/SelectCell already
      // use to fill their cell — "the padding is in the cell, not around it"
      // (Alex, 2026-08-27: "can the image take the full real estate with
      // minimum padding"). Coupled to the token's CURRENT value on purpose (CSS
      // cannot invert a shorthand var); re-measure if --th-cell-pad ever moves.
      // Still inside the <td>'s own border box, so its overflow:hidden does not
      // clip this — only content past the FULL box (padding included) would be.
      return (
        <div style={{ margin: -6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ImageCell dbId={dbId} onshapeRef={row.onshape_ref} size={40} />
        </div>
      );
    },
  });

  if (seq) {
    cols.push({
      key: seqKey, label: '#', width: 36, align: 'right', resizable: false,
      render: row => (
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--tg-icon-color)', fontSize: 'var(--fs-small)' }}>
          {row[seqKey]}
        </span>
      ),
    });
  }

  if (parentCol) cols.push(parentCol);

  cols.push(
    {
      key: 'code', label: 'Tag', width: 90, editable, addField: editable, textKind: 'code',
      render: row => <Text kind="code">{row.code}</Text>,
    },
    {
      key: 'label', label: 'Label', width: 230, editable, addField: editable,
      editValue: row => row.label ?? '',
      // INF-8 — a project's own product says so IN WORDS (Alex is colour-blind):
      // "<project> only". Standard rows and category rows say nothing.
      render: row => <OwnerLabel label={row.label} ownerOnly={row._ownerOnly} onPromote={onPromote ? () => onPromote(row) : undefined} />,
    },
  );

  if (items) {
    cols.push({
      key: '_items', label: 'Items', width: 64, align: 'right',
      render: row => {
        // Mixed grids: a leaf row has no children — leave the cell empty.
        if (statusMode === 'auto' && row._type === 'product') return null;
        return <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>{items.value(row)}</span>;
      },
    });
  }

  cols.push({
    key: 'status', label: 'Status', width: statusEditable ? 90 : 190, align: 'left', textKind: 'small',
    ...(statusEditable ? { editType: 'select', options: ['draft', 'active', 'deprecated'] } : {}),
    render: row => {
      if (statusMode === 'leaf' || (statusMode === 'auto' && row._type === 'product')) {
        return <ProductLeafStatus row={row} />;
      }
      // Mixed grids: a catalog FAMILY leaf carries one catalog-vocabulary status
      // of its own (validated_db/…), shown with the same StatusCell as line rows.
      if (statusMode === 'auto' && row._type === 'family') {
        return <span style={{ fontSize: 'var(--fs-small)' }}><StatusCell value={row.status} /></span>;
      }
      return <CategoryStatus row={row} />;
    },
  });

  if (onshapeCol) {
    cols.push({
      key: 'onshape', label: '', width: 22, align: 'center',
      render: row => <OnshapeLink ref={row.onshape_ref} />,
    });
  }

  cols.push(...extraCols);
  return cols;
}
